import { PropsWithChildren, useState, useCallback, useMemo, useEffect, useContext, createContext } from "react";
import { isAuthenticated, setAccessToken } from "../service/axios";
import { IUser } from "../service/types";
import { useUpdateProfileMutation } from "../service/user/hooks";

interface IProfileContext {
  profile: IUser | null;
  isLoading: boolean;
  updateProfile: (data: Record<string, string | boolean>) => void;
  updateProfileImage: (file: File) => void;
  logout: () => void;
}

const defaultProfileContextData: IProfileContext = {
  profile: null,
  updateProfile: () => {},
  updateProfileImage: () => {},
  logout: () => {},
  isLoading: false,
};

const ProfileContext = createContext(defaultProfileContextData);

export function ProfileProvider({ children }: PropsWithChildren) {
  const [data, setData] = useState(defaultProfileContextData);

  const onUpdateSuccess = useCallback(
    (profile: IUser | null) => {
      if (!profile) return;

      localStorage.setItem('profile', JSON.stringify(profile));
      setData((data) => ({ ...data, profile }));
    },
    [data]
  );
  const updateProfileMutation = useUpdateProfileMutation({
    onSuccess: onUpdateSuccess,
  });
  const isLoading = useMemo(() => updateProfileMutation.isLoading, [updateProfileMutation]);
  const updateProfile = useCallback(
    (data: Record<string, string | boolean>) => {
      setData((data) => ({
        ...data,
      }));
      updateProfileMutation.mutate(data);
    },
    [updateProfileMutation]
  );

  const updateProfileImage = useCallback(
    (file: File) => {
      const data: any = new FormData();
      data.append('image', file);
      updateProfileMutation.mutate(data);
    },
    [updateProfileMutation]
  );

  useEffect(() => {
    if (!isAuthenticated()) return;
    const savedProfile = JSON.parse(localStorage.getItem('profile') || 'null');
    if (savedProfile) setData((data) => ({ ...data, profile: savedProfile }));
    updateProfileMutation.mutate({});
  }, []);

  return (
    <ProfileContext.Provider
      value={{
        ...data,
        isLoading: isLoading,
        updateProfile,
        updateProfileImage,
        logout() {
          const existingUrlData = localStorage.getItem('urlData');

          setAccessToken(null, null);
          localStorage.clear();
          localStorage.setItem('geolocation_permission', 'true');
          if (existingUrlData) localStorage.setItem('urlData', existingUrlData);

          window.location.reload();
        },
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
